import { FC, FormEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { computeMonth, fetchMonth, fetchMonthOwnerBalances, MonthOwnerBalance } from '../services/monthsService';
import {
  createTransaction,
  listTransactions,
  Transaction,
  TransactionCreatePayload,
  TransactionOwner,
} from '../services/transactionsService';
import {
  createOwnerDraw,
  deleteOwnerDraw,
  listOwnerDraws,
  OwnerDraw,
  OwnerDrawCreatePayload,
} from '../services/ownerDrawsService';
import { Month } from '../types/month';
import { CUSTOM_OPTION_VALUE, TRANSACTION_TYPE_OPTIONS } from '../constants/transactions';
import { formatDate } from '../utils/format';

const ownerLabels: Record<TransactionOwner, string> = {
  gaurav: 'Gaurav',
  sourabh: 'Sourabh',
  company: 'Company',
};

const owners: TransactionOwner[] = ['gaurav', 'sourabh', 'company'];

const toMonthNumber = (input: string) => {
  const [year, month] = input.split('-').map(Number);
  return { year, month };
};

const currentMonthISO = () => {
  const now = new Date();
  // Business period: 11th → 10th counts toward the starting month.
  const periodAnchor = now.getDate() < 11 ? new Date(now.getFullYear(), now.getMonth() - 1, 1) : new Date(now.getFullYear(), now.getMonth(), 1);
  return `${periodAnchor.getFullYear()}-${String(periodAnchor.getMonth() + 1).padStart(2, '0')}`;
};

type QuickEntryState = {
  date: string;
  month: string;
  amount: string;
  type: string;
  owner: TransactionOwner;
  remarks: string;
};

export const MonthPage = () => {
  const { token } = useAuth();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [monthFilter, setMonthFilter] = useState(currentMonthISO());
  const [monthSummary, setMonthSummary] = useState<Month | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [ownerDraws, setOwnerDraws] = useState<OwnerDraw[]>([]);
  const [ownerBalances, setOwnerBalances] = useState<Record<string, MonthOwnerBalance>>({});
  const [loading, setLoading] = useState(false);
  const [monthChanging, setMonthChanging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [recomputeConfirm, setRecomputeConfirm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; drawId: number | null }>({
    isOpen: false,
    drawId: null,
  });
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [debitModalOpen, setDebitModalOpen] = useState(false);
  const [ownerDrawModalOpen, setOwnerDrawModalOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const defaultCreditType = TRANSACTION_TYPE_OPTIONS[0] ?? '';
  const defaultDebitType = TRANSACTION_TYPE_OPTIONS.includes('Bank') ? 'Bank' : defaultCreditType;
  const [quickCredit, setQuickCredit] = useState<QuickEntryState>({
    date: new Date().toISOString().slice(0, 10),
    month: currentMonthISO(),
    amount: '',
    type: defaultCreditType,
    owner: 'company',
    remarks: '',
  });
  const [quickDebit, setQuickDebit] = useState<QuickEntryState>({
    date: new Date().toISOString().slice(0, 10),
    month: currentMonthISO(),
    amount: '',
    type: defaultDebitType,
    owner: 'company',
    remarks: '',
  });
  const [quickOwnerDraw, setQuickOwnerDraw] = useState<Omit<QuickEntryState, 'type'>>({
    date: new Date().toISOString().slice(0, 10),
    month: currentMonthISO(),
    amount: '',
    owner: 'gaurav',
    remarks: '',
  });
  const [saving, setSaving] = useState(false);

  const creditTransactions = useMemo(
    () => transactions.filter((transaction) => Number(transaction.credit_amount) > 0),
    [transactions],
  );
  const debitTransactions = useMemo(
    () => transactions.filter((transaction) => Number(transaction.debit_amount) > 0),
    [transactions],
  );

  const totals = useMemo(() => {
    return transactions.reduce(
      (acc, tx) => {
        acc.credit += Number(tx.credit_amount);
        acc.debit += Number(tx.debit_amount);
        return acc;
      },
      { credit: 0, debit: 0 },
    );
  }, [transactions]);

  const ownerTotals = useMemo(() => {
    const base = {
      gaurav: { credit: 0, debit: 0 },
      sourabh: { credit: 0, debit: 0 },
      company: { credit: 0, debit: 0 },
    } as Record<TransactionOwner, { credit: number; debit: number }>;

    for (const tx of transactions) {
      const owner = tx.owner as TransactionOwner;
      const credit = Number(tx.credit_amount ?? 0);
      const debit = Number(tx.debit_amount ?? 0);
      base[owner].credit += credit;
      base[owner].debit += debit;
    }
    return base;
  }, [transactions]);

  // Filters and sorting for credits/expenses
  type SortKey = 'date' | 'amount' | 'type' | 'owner';
  const [creditFilterOwner, setCreditFilterOwner] = useState<TransactionOwner | 'all'>('all');
  const [creditFilterType, setCreditFilterType] = useState<string | 'all'>('all');
  const [creditFilterQuery, setCreditFilterQuery] = useState('');
  const [creditSortKey, setCreditSortKey] = useState<SortKey>('date');
  const [creditSortDir, setCreditSortDir] = useState<'asc' | 'desc'>('desc');

  const [debitFilterOwner, setDebitFilterOwner] = useState<TransactionOwner | 'all'>('all');
  const [debitFilterType, setDebitFilterType] = useState<string | 'all'>('all');
  const [debitFilterQuery, setDebitFilterQuery] = useState('');
  const [debitSortKey, setDebitSortKey] = useState<SortKey>('date');
  const [debitSortDir, setDebitSortDir] = useState<'asc' | 'desc'>('desc');

  function normalizeString(v?: string | null): string {
    return (v ?? '').toString().toLowerCase();
  }

  function sortTx(list: Transaction[], key: SortKey, dir: 'asc' | 'desc', isCredit: boolean): Transaction[] {
    const factor = dir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      switch (key) {
        case 'date':
          return (new Date(a.date).getTime() - new Date(b.date).getTime()) * factor;
        case 'amount': {
          const av = Number(isCredit ? a.credit_amount : a.debit_amount);
          const bv = Number(isCredit ? b.credit_amount : b.debit_amount);
          return (av - bv) * factor;
        }
        case 'type':
          return normalizeString(a.type).localeCompare(normalizeString(b.type)) * factor;
        case 'owner':
          return normalizeString(a.owner as unknown as string).localeCompare(normalizeString(b.owner as unknown as string)) * factor;
      }
    });
  }

  const creditView = useMemo(() => {
    const filtered = creditTransactions.filter((tx) => {
      if (creditFilterOwner !== 'all' && tx.owner !== creditFilterOwner) return false;
      if (creditFilterType !== 'all' && normalizeString(tx.type) !== normalizeString(creditFilterType)) return false;
      if (creditFilterQuery && !normalizeString(tx.remarks).includes(normalizeString(creditFilterQuery))) return false;
      return true;
    });
    return sortTx(filtered, creditSortKey, creditSortDir, true);
  }, [creditTransactions, creditFilterOwner, creditFilterType, creditFilterQuery, creditSortKey, creditSortDir]);

  const debitView = useMemo(() => {
    const filtered = debitTransactions.filter((tx) => {
      if (debitFilterOwner !== 'all' && tx.owner !== debitFilterOwner) return false;
      if (debitFilterType !== 'all' && normalizeString(tx.type) !== normalizeString(debitFilterType)) return false;
      if (debitFilterQuery && !normalizeString(tx.remarks).includes(normalizeString(debitFilterQuery))) return false;
      return true;
    });
    return sortTx(filtered, debitSortKey, debitSortDir, false);
  }, [debitTransactions, debitFilterOwner, debitFilterType, debitFilterQuery, debitSortKey, debitSortDir]);

  const loadMonth = async () => {
    if (!token) return;
    setLoading(true);
    setMonthChanging(true);
    setError(null);
    const { year, month } = toMonthNumber(monthFilter);

    try {
      const [monthResponse, transactionsResponse, balancesResponse, ownerDrawsResponse] = await Promise.all([
        fetchMonth(token, year, month),
        listTransactions(token, { month: monthFilter }),
        fetchMonthOwnerBalances(token, year, month),
        listOwnerDraws(token, { month: monthFilter }),
      ]);

      setMonthSummary(monthResponse.data);
      setTransactions(transactionsResponse.data);
      setOwnerBalances(balancesResponse.data);
      setOwnerDraws(ownerDrawsResponse.data);
    } catch (err) {
      if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Failed to load month data.', 'error');
      }
    } finally {
      setLoading(false);
      setMonthChanging(false);
    }
  };

  useEffect(() => {
    loadMonth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthFilter, token]);

  useEffect(() => {
    const state = (location.state as { openQuick?: 'credit' | 'expense' } | null) ?? null;
    if (state?.openQuick === 'credit') {
      setCreditModalOpen(true);
      // clear state after handling
      navigate('.', { replace: true, state: {} });
    } else if (state?.openQuick === 'expense') {
      setDebitModalOpen(true);
      navigate('.', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    const handleOpenCredit = () => setCreditModalOpen(true);
    const handleOpenExpense = () => setDebitModalOpen(true);
    window.addEventListener('openAddCredit', handleOpenCredit as EventListener);
    window.addEventListener('openAddExpense', handleOpenExpense as EventListener);
    return () => {
      window.removeEventListener('openAddCredit', handleOpenCredit as EventListener);
      window.removeEventListener('openAddExpense', handleOpenExpense as EventListener);
    };
  }, []);

  const handleQuickEntryChange =
    (setter: React.Dispatch<React.SetStateAction<QuickEntryState>>, field: keyof QuickEntryState) =>
    (value: string) => {
      setter((prev) => ({ ...prev, [field]: value }));
    };

  const handleOwnerDrawSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!token) return;

      setSaving(true);

      try {
        const payload: OwnerDrawCreatePayload = {
          date: quickOwnerDraw.date,
          month: quickOwnerDraw.month,
          amount: Number(quickOwnerDraw.amount),
          owner: quickOwnerDraw.owner,
          remarks: quickOwnerDraw.remarks || undefined,
        };

        await createOwnerDraw(token, payload);
        showToast('Owner draw created successfully.', 'success');
      setQuickOwnerDraw({
        date: new Date().toISOString().slice(0, 10),
        month: currentMonthISO(),
        amount: '',
        owner: 'gaurav',
        remarks: '',
      });
      setOwnerDrawModalOpen(false);
      loadMonth();
    } catch (err) {
      if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Failed to create owner draw.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleQuickSubmit =
    (
      setter: React.Dispatch<React.SetStateAction<QuickEntryState>>,
      mode: 'credit' | 'debit',
      onSuccess?: () => void,
    ) =>
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!token) return;

      setSaving(true);

      const state = mode === 'credit' ? quickCredit : quickDebit;
      const amount = Number(state.amount || 0);

      if (amount <= 0) {
        showToast('Amount must be greater than zero.', 'error');
        setSaving(false);
        return;
      }

      const normalizedType = state.type.trim();
      if (!normalizedType) {
        showToast('Select or enter a transaction type.', 'error');
        setSaving(false);
        return;
      }

      const payload: TransactionCreatePayload = {
        date: state.date,
        month: state.month,
        credit_amount: mode === 'credit' ? amount : 0,
        debit_amount: mode === 'debit' ? amount : 0,
        type: normalizedType,
        owner: state.owner,
        remarks: state.remarks,
      };

      try {
        await createTransaction(token, payload);
        setter((prev) => ({
          ...prev,
          amount: '',
          remarks: '',
          type: normalizedType,
        }));
        showToast(mode === 'credit' ? 'Credit recorded successfully.' : 'Expense recorded successfully.', 'success');
        onSuccess?.();
        await loadMonth();
      } catch (err) {
        if (err instanceof Error) {
          showToast(err.message, 'error');
        } else {
          showToast('Unable to save transaction.', 'error');
        }
      } finally {
        setSaving(false);
      }
    };

  const handleRecompute = async () => {
    if (!token) return;
    const { year, month } = toMonthNumber(monthFilter);

    let force = false;
    if (monthSummary?.is_locked) {
      setRecomputeConfirm(true);
      return;
    }

    performRecompute(force);
  };

  const performRecompute = async (force: boolean = false) => {
    if (!token) return;
    const { year, month } = toMonthNumber(monthFilter);

    setRecomputing(true);

    try {
      const response = await computeMonth(token, year, month, force);
      setMonthSummary(response.data);
      showToast('Allocations recomputed successfully.', 'success');
    } catch (err) {
      if (err instanceof Error) {
        showToast(err.message, 'error');
      } else {
        showToast('Unable to recompute allocations.', 'error');
      }
    } finally {
      setRecomputing(false);
    }
  };

  const isCreditTypeCustom = !TRANSACTION_TYPE_OPTIONS.includes(quickCredit.type);
  const isDebitTypeCustom = !TRANSACTION_TYPE_OPTIONS.includes(quickDebit.type);
  const allocations = monthSummary?.allocations ?? [];
  const locked = monthSummary?.is_locked ?? false;
  
  // Extract Gaurav and Sourabh allocations for display in summary cards
  const gauravAllocation = allocations.find((a) => {
    const owner = typeof a.owner === 'string' ? a.owner : String(a.owner);
    return owner === 'gaurav' || owner.toLowerCase() === 'gaurav';
  });
  const sourabhAllocation = allocations.find((a) => {
    const owner = typeof a.owner === 'string' ? a.owner : String(a.owner);
    return owner === 'sourabh' || owner.toLowerCase() === 'sourabh';
  });
  const gauravProfit = Number(gauravAllocation?.allocation_amount ?? 0);
  const sourabhProfit = Number(sourabhAllocation?.allocation_amount ?? 0);
  const gauravPercentage = Number(gauravAllocation?.allocation_percentage ?? 0);
  const sourabhPercentage = Number(sourabhAllocation?.allocation_percentage ?? 0);
  const computedAtText = monthSummary?.computed_at
    ? new Date(monthSummary.computed_at).toLocaleString()
    : 'Not computed yet';

  return (
    <AppLayout
      title="Month Overview"
      toolbar={
        <div className="month-toolbar" style={{ 
          display: 'flex', 
          flexDirection: 'row', 
          gap: 12, 
          width: '100%', 
          boxSizing: 'border-box', 
          alignItems: 'flex-end',
          flexWrap: 'wrap'
        }}>
          <button
            className="emulate-btn btn"
            type="button"
            onClick={handleRecompute}
            disabled={recomputing || monthChanging}
            style={{ 
              flexShrink: 0, 
              padding: '12px 16px', 
              whiteSpace: 'nowrap',
              minWidth: 'fit-content'
            }}
          >
            {recomputing ? 'Recomputing…' : 'Recompute allocations'}
          </button>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 8, 
            flex: '1 1 200px', 
            minWidth: 0,
            maxWidth: '100%'
          }}>
            <label className="form-label" style={{ marginBottom: 0 }}>
              Month
            </label>
            <input
              type="month"
              className="input"
              value={monthFilter}
              onChange={(event) => setMonthFilter(event.target.value)}
              disabled={monthChanging}
              style={{ 
                width: '100%', 
                maxWidth: '100%', 
                boxSizing: 'border-box',
                minWidth: 0
              }}
            />
          </div>
        </div>
      }
    >
      {monthChanging && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          background: 'rgba(255, 255, 255, 0.9)', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          zIndex: 2000,
          gap: 16
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(90deg, #0b4d99 0%, #1fa2ff 50%, #0b4d99 100%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            boxShadow: '0 8px 24px rgba(11, 77, 153, 0.3)',
          }} />
          <div style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--brand-primary)' }}>Loading month data...</div>
          <style>{`
            @keyframes shimmer {
              0% { background-position: -200% 0; }
              100% { background-position: 200% 0; }
            }
          `}</style>
        </div>
      )}

      <section className="cards month-summary-cards" style={{ gap: 14 }}>
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: 0 }}>Credits</h3>
          <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '6px 0' }}>
            ₹ {totals.credit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
          <span className="subtle-text">Total inflow this month</span>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: 0 }}>Debits</h3>
          <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '6px 0' }}>
            ₹ {totals.debit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
          <span className="subtle-text">Total outflow this month</span>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: 0 }}>Net Profit</h3>
          <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '6px 0' }}>
            ₹ {Number(totals.credit - totals.debit).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
          <span className="subtle-text">Without salaries and adjustments</span>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: 0 }}>Gaurav Profit</h3>
          <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '6px 0' }}>
            ₹ {gauravProfit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
          <span className="subtle-text">
            {gauravPercentage > 0 ? `${gauravPercentage.toLocaleString('en-IN', { maximumFractionDigits: 2 })}% share` : 'No allocation yet'}
          </span>
        </div>
        <div className="card" style={{ padding: 16 }}>
          <h3 style={{ margin: 0 }}>Sourabh Profit</h3>
          <p style={{ fontSize: '1.3rem', fontWeight: 600, margin: '6px 0' }}>
            ₹ {sourabhProfit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
          </p>
          <span className="subtle-text">
            {sourabhPercentage > 0 ? `${sourabhPercentage.toLocaleString('en-IN', { maximumFractionDigits: 2 })}% share` : 'No allocation yet'}
          </span>
        </div>
      </section>

      <section className="cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
        <h3 style={{ gridColumn: '1 / -1', margin: '0 0 12px', fontSize: '1.1rem', fontWeight: 600 }}>Month Transactions Summary</h3>
        {(['gaurav', 'sourabh', 'company'] as TransactionOwner[]).map((ownerKey) => {
          const o = ownerTotals[ownerKey];
          const ownerLabel = ownerLabels[ownerKey];
          return (
            <div className="card" key={`owner-totals-${ownerKey}`} style={{ padding: 16 }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>{ownerLabel}</h3>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                <span className="subtle-text">Received</span>
                <strong style={{ fontSize: '1.05rem' }}>₹ {o.credit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="subtle-text">Spent</span>
                <strong style={{ fontSize: '1.05rem' }}>₹ {o.debit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="subtle-text">Net</span>
                <strong style={{ fontSize: '1.05rem' }}>₹ {(o.credit - o.debit).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
              </div>
            </div>
          );
        })}
      </section>

      <section className="cards" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginTop: 18 }}>
        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>Owner Balances (Cumulative)</h3>
          <button
            className="btn"
            onClick={() => setOwnerDrawModalOpen(true)}
            style={{ fontSize: '0.9rem', padding: '8px 16px' }}
          >
            + Add Owner Draw
          </button>
        </div>
        {(['gaurav', 'sourabh', 'company'] as TransactionOwner[]).map((ownerKey) => {
          const balance = ownerBalances[ownerKey] ?? {
            previous_month_balance: 0,
            current_month_earnings: 0,
            current_month_expenses: 0,
            current_month_profit_allocation: 0,
            current_month_balance: 0,
          };
          const ownerLabel = ownerKey === 'gaurav' ? 'Gaurav N' : ownerKey === 'sourabh' ? 'Sourabh S' : 'Company';
          return (
            <div className="card" key={`owner-balance-${ownerKey}`} style={{ padding: 16, background: 'linear-gradient(135deg, rgba(11, 77, 153, 0.04) 0%, rgba(31, 162, 255, 0.04) 100%)' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>{ownerLabel}</h3>
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span className="subtle-text">Previous Month Balance:</span>
                  <strong>₹ {balance.previous_month_balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
                {(balance.current_month_opening_balance ?? 0) > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                    <span className="subtle-text">Opening Balance (This Month):</span>
                    <strong style={{ color: '#15803d' }}>+ ₹ {(balance.current_month_opening_balance ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                  </div>
                )}
                {ownerKey === 'company' ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Earnings:</span>
                      <strong style={{ color: '#15803d' }}>+ ₹ {balance.current_month_earnings.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Expenses:</span>
                      <strong style={{ color: '#dc2626' }}>- ₹ {balance.current_month_expenses.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Earnings:</span>
                      <strong style={{ color: '#dc2626' }}>- ₹ {balance.current_month_earnings.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                      <span className="subtle-text">This Month Expenses:</span>
                      <strong style={{ color: '#15803d' }}>+ ₹ {balance.current_month_expenses.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                    </div>
                  </>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                  <span className="subtle-text">Profit Allocation:</span>
                  <strong style={{ color: '#15803d' }}>+ ₹ {balance.current_month_profit_allocation.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                </div>
                {balance.current_month_owner_draws !== undefined && balance.current_month_owner_draws > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                    <span className="subtle-text">Owner Draws:</span>
                    <strong style={{ color: '#dc2626' }}>- ₹ {balance.current_month_owner_draws.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</strong>
                  </div>
                )}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(15, 35, 95, 0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 600 }}>Current Month Balance:</span>
                    <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: balance.current_month_balance >= 0 ? '#15803d' : '#dc2626' }}>
                      ₹ {balance.current_month_balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </h3>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="cards" style={{ marginTop: 18, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {allocations.length === 0 ? (
          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <span className="subtle-text">No allocations yet. Recompute to generate them.</span>
          </div>
        ) : (
          allocations.map((allocation) => {
            const ownerKey = allocation.owner as TransactionOwner;
            const ownerLabel = ownerLabels[ownerKey] ?? allocation.owner;
            const amountDisplay = Number(allocation.allocation_amount ?? 0).toLocaleString('en-IN', {
              maximumFractionDigits: 2,
            });
            const percentageDisplay = Number(allocation.allocation_percentage ?? 0).toLocaleString('en-IN', {
              maximumFractionDigits: 2,
            });

            return (
              <div
                className="card"
                key={allocation.id ?? allocation.owner}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                <h4 style={{ margin: 0, fontSize: '1.1rem' }}>{ownerLabel}</h4>
                <p style={{ fontSize: '1.6rem', fontWeight: 600, margin: '6px 0' }}>₹ {amountDisplay}</p>
                <span className="subtle-text">{percentageDisplay}% share</span>
                {allocation.is_manual && <span className="badge">Manual override</span>}
                {allocation.note && <span className="subtle-text">{allocation.note}</span>}
              </div>
            );
          })
        )}
      </section>

      <section className="card" style={{ padding: 0 }}>
        <header style={{ padding: '24px 24px 0', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ margin: 0 }}>Credits</h3>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label className="subtle-text" style={{ marginRight: 6, flexShrink: 0 }}>Sort</label>
              <select className="input" value={creditSortKey} onChange={(e) => setCreditSortKey(e.target.value as SortKey)} style={{ width: 130, flexShrink: 0 }}>
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="type">Type</option>
                <option value="owner">Owner</option>
              </select>
              <select className="input" value={creditSortDir} onChange={(e) => setCreditSortDir(e.target.value as 'asc' | 'desc')} style={{ width: 120, flexShrink: 0 }}>
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
              <span className="pill" style={{ flexShrink: 0 }}>
                Total ₹ {creditTransactions.reduce((acc, tx) => acc + Number(tx.credit_amount), 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </header>
        <div className="table">
          <div className="table-filter-row" style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px' }}>
            <span />
            <span />
            <select className="input" value={creditFilterType} onChange={(e) => setCreditFilterType(e.target.value as any)}>
              <option value="all">All types</option>
              {TRANSACTION_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select className="input" value={creditFilterOwner} onChange={(e) => setCreditFilterOwner(e.target.value as any)}>
              <option value="all">All owners</option>
              {owners.map((o) => <option key={o} value={o}>{ownerLabels[o]}</option>)}
            </select>
            <input className="input" placeholder="Search remarks" value={creditFilterQuery} onChange={(e) => setCreditFilterQuery(e.target.value)} />
          </div>
          <div className="table-head" style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px' }}>
            <span>Date</span>
            <span>Amount (₹)</span>
            <span>Type</span>
            <span>Owner</span>
            <span>Remarks</span>
          </div>
          {loading ? (
            <>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="table-row" style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px' }}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div
                      key={j}
                      style={{
                        height: 16,
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                        borderRadius: 4,
                        width: j === 4 ? '80%' : '100%',
                      }}
                    />
                  ))}
                </div>
              ))}
            </>
          ) : creditView.length === 0 ? (
            <div className="table-row">
              <span style={{ gridColumn: '1 / -1', textAlign: 'center' }}>No credits recorded.</span>
            </div>
          ) : (
            creditView.map((transaction) => (
              <div
                className="table-row"
                key={transaction.id}
                style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px', alignItems: 'center' }}
              >
                <span>{formatDate(transaction.date)}</span>
                <span>{Number(transaction.credit_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{transaction.type || '—'}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{ownerLabels[transaction.owner]}</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={transaction.remarks || undefined}>{transaction.remarks || '—'}</span>
              </div>
            ))
          )}
        </div>
      </section>

      <section className="card" style={{ padding: 0 }}>
        <header style={{ padding: '24px 24px 0', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'stretch' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ margin: 0 }}>Expenses</h3>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <label className="subtle-text" style={{ marginRight: 6, flexShrink: 0 }}>Sort</label>
              <select className="input" value={debitSortKey} onChange={(e) => setDebitSortKey(e.target.value as SortKey)} style={{ width: 130, flexShrink: 0 }}>
                <option value="date">Date</option>
                <option value="amount">Amount</option>
                <option value="type">Type</option>
                <option value="owner">Owner</option>
              </select>
              <select className="input" value={debitSortDir} onChange={(e) => setDebitSortDir(e.target.value as 'asc' | 'desc')} style={{ width: 120, flexShrink: 0 }}>
                <option value="desc">Desc</option>
                <option value="asc">Asc</option>
              </select>
              <span className="pill" style={{ flexShrink: 0 }}>
                Total ₹ {debitTransactions.reduce((acc, tx) => acc + Number(tx.debit_amount), 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>
        </header>
        <div className="table">
          <div className="table-filter-row" style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px' }}>
            <span />
            <span />
            <select className="input" value={debitFilterType} onChange={(e) => setDebitFilterType(e.target.value as any)}>
              <option value="all">All types</option>
              {TRANSACTION_TYPE_OPTIONS.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select className="input" value={debitFilterOwner} onChange={(e) => setDebitFilterOwner(e.target.value as any)}>
              <option value="all">All owners</option>
              {owners.map((o) => <option key={o} value={o}>{ownerLabels[o]}</option>)}
            </select>
            <input className="input" placeholder="Search remarks" value={debitFilterQuery} onChange={(e) => setDebitFilterQuery(e.target.value)} />
          </div>
          <div className="table-head" style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px' }}>
            <span>Date</span>
            <span>Amount (₹)</span>
            <span>Type</span>
            <span>Owner</span>
            <span>Remarks</span>
          </div>
          {loading ? (
            <>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="table-row" style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px' }}>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div
                      key={j}
                      style={{
                        height: 16,
                        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite',
                        borderRadius: 4,
                        width: j === 4 ? '80%' : '100%',
                      }}
                    />
                  ))}
                </div>
              ))}
            </>
          ) : debitView.length === 0 ? (
            <div className="table-row">
              <span style={{ gridColumn: '1 / -1', textAlign: 'center' }}>No expenses recorded.</span>
            </div>
          ) : (
            debitView.map((transaction) => (
              <div
                className="table-row"
                key={transaction.id}
                style={{ gridTemplateColumns: '130px 120px 0.8fr 120px 2fr', gap: '10px', alignItems: 'center' }}
              >
                <span>{formatDate(transaction.date)}</span>
                <span>{Number(transaction.debit_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{transaction.type || '—'}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{ownerLabels[transaction.owner]}</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={transaction.remarks || undefined}>{transaction.remarks || '—'}</span>
              </div>
            ))
          )}
        </div>
      </section>

      {ownerDraws.length > 0 && (
        <section className="card" style={{ marginTop: 18, padding: 0 }}>
          <header style={{ padding: '24px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Owner Draws</h3>
            <span className="pill">
              Total ₹ {ownerDraws.reduce((acc, draw) => acc + Number(draw.amount), 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
            </span>
          </header>
          <div className="table">
            <div className="table-head" style={{ gridTemplateColumns: '130px 120px 120px 2fr 80px', gap: '10px' }}>
              <span>Date</span>
              <span>Amount (₹)</span>
              <span>Owner</span>
              <span>Remarks</span>
              <span>Actions</span>
            </div>
            {ownerDraws.map((draw) => (
              <div
                className="table-row"
                key={draw.id}
                style={{ gridTemplateColumns: '130px 120px 120px 2fr 80px', gap: '10px', alignItems: 'center' }}
              >
                <span>{formatDate(draw.date)}</span>
                <span>{Number(draw.amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                <span style={{ whiteSpace: 'nowrap' }}>{ownerLabels[draw.owner]}</span>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={draw.remarks || undefined}>{draw.remarks || '—'}</span>
                <span>
                  <button
                    className="btn"
                    style={{ fontSize: '0.85rem', padding: '4px 8px' }}
                    onClick={() => {
                      setDeleteConfirm({ isOpen: true, drawId: draw.id });
                    }}
                  >
                    Delete
                  </button>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {creditModalOpen && (
        <Modal title="Quick credit" onClose={() => setCreditModalOpen(false)}>
          <form
            onSubmit={handleQuickSubmit(setQuickCredit, 'credit', () => setCreditModalOpen(false))}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <input
              type="date"
              className="input"
              value={quickCredit.date}
              onChange={(event) => handleQuickEntryChange(setQuickCredit, 'date')(event.target.value)}
              required
            />
            <input
              type="month"
              className="input"
              value={quickCredit.month}
              onChange={(event) => handleQuickEntryChange(setQuickCredit, 'month')(event.target.value)}
              required
            />
            <input
              type="number"
              className="input"
              placeholder="Amount (₹)"
              min="0"
              step="0.01"
              value={quickCredit.amount}
              onChange={(event) => handleQuickEntryChange(setQuickCredit, 'amount')(event.target.value)}
              required
            />
            <select
              className="input"
              value={isCreditTypeCustom ? CUSTOM_OPTION_VALUE : quickCredit.type}
              onChange={(event) => {
                const value = event.target.value;
                if (value === CUSTOM_OPTION_VALUE) {
                  handleQuickEntryChange(setQuickCredit, 'type')('');
                } else {
                  handleQuickEntryChange(setQuickCredit, 'type')(value);
                }
              }}
            >
              {TRANSACTION_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              <option value={CUSTOM_OPTION_VALUE}>Custom type…</option>
            </select>
            {isCreditTypeCustom && (
              <input
                type="text"
                className="input"
                placeholder="Enter custom type"
                value={quickCredit.type}
                onChange={(event) => handleQuickEntryChange(setQuickCredit, 'type')(event.target.value)}
              />
            )}
            <select
              className="input"
              value={quickCredit.owner}
              onChange={(event) =>
                handleQuickEntryChange(setQuickCredit, 'owner')(event.target.value as TransactionOwner)
              }
            >
              {owners.map((owner) => (
                <option key={owner} value={owner}>
                  {ownerLabels[owner]}
                </option>
              ))}
            </select>
            <input
              type="text"
              className="input"
              placeholder="Remarks"
              value={quickCredit.remarks}
              onChange={(event) => handleQuickEntryChange(setQuickCredit, 'remarks')(event.target.value)}
            />
            <button className="btn" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save credit'}
            </button>
          </form>
        </Modal>
      )}

      {debitModalOpen && (
        <Modal title="Quick expense" onClose={() => setDebitModalOpen(false)}>
          <form
            onSubmit={handleQuickSubmit(setQuickDebit, 'debit', () => setDebitModalOpen(false))}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <input
              type="date"
              className="input"
              value={quickDebit.date}
              onChange={(event) => handleQuickEntryChange(setQuickDebit, 'date')(event.target.value)}
              required
            />
            <input
              type="month"
              className="input"
              value={quickDebit.month}
              onChange={(event) => handleQuickEntryChange(setQuickDebit, 'month')(event.target.value)}
              required
            />
            <input
              type="number"
              className="input"
              placeholder="Amount (₹)"
              min="0"
              step="0.01"
              value={quickDebit.amount}
              onChange={(event) => handleQuickEntryChange(setQuickDebit, 'amount')(event.target.value)}
              required
            />
            <select
              className="input"
              value={isDebitTypeCustom ? CUSTOM_OPTION_VALUE : quickDebit.type}
              onChange={(event) => {
                const value = event.target.value;
                if (value === CUSTOM_OPTION_VALUE) {
                  handleQuickEntryChange(setQuickDebit, 'type')('');
                } else {
                  handleQuickEntryChange(setQuickDebit, 'type')(value);
                }
              }}
            >
              {TRANSACTION_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
              <option value={CUSTOM_OPTION_VALUE}>Custom type…</option>
            </select>
            {isDebitTypeCustom && (
              <input
                type="text"
                className="input"
                placeholder="Enter custom type"
                value={quickDebit.type}
                onChange={(event) => handleQuickEntryChange(setQuickDebit, 'type')(event.target.value)}
              />
            )}
            <select
              className="input"
              value={quickDebit.owner}
              onChange={(event) =>
                handleQuickEntryChange(setQuickDebit, 'owner')(event.target.value as TransactionOwner)
              }
            >
              {owners.map((owner) => (
                <option key={owner} value={owner}>
                  {ownerLabels[owner]}
                </option>
              ))}
            </select>
            <input
              type="text"
              className="input"
              placeholder="Remarks"
              value={quickDebit.remarks}
              onChange={(event) => handleQuickEntryChange(setQuickDebit, 'remarks')(event.target.value)}
            />
            <button className="btn" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save expense'}
            </button>
          </form>
        </Modal>
      )}

      {ownerDrawModalOpen && (
        <Modal title="Add Owner Draw" onClose={() => setOwnerDrawModalOpen(false)}>
          <form
            onSubmit={handleOwnerDrawSubmit}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <input
              type="date"
              className="input"
              value={quickOwnerDraw.date}
              onChange={(event) => handleQuickEntryChange(setQuickOwnerDraw as any, 'date')(event.target.value)}
              required
            />
            <input
              type="month"
              className="input"
              value={quickOwnerDraw.month}
              onChange={(event) => handleQuickEntryChange(setQuickOwnerDraw as any, 'month')(event.target.value)}
              required
            />
            <input
              type="number"
              className="input"
              placeholder="Amount (₹)"
              min="0.01"
              step="0.01"
              value={quickOwnerDraw.amount}
              onChange={(event) => handleQuickEntryChange(setQuickOwnerDraw as any, 'amount')(event.target.value)}
              required
            />
            <select
              className="input"
              value={quickOwnerDraw.owner}
              onChange={(event) =>
                handleQuickEntryChange(setQuickOwnerDraw as any, 'owner')(event.target.value as TransactionOwner)
              }
            >
              {(['gaurav', 'sourabh'] as TransactionOwner[]).map((owner) => (
                <option key={owner} value={owner}>
                  {ownerLabels[owner]}
                </option>
              ))}
            </select>
            <input
              type="text"
              className="input"
              placeholder="Remarks (optional)"
              value={quickOwnerDraw.remarks}
              onChange={(event) => handleQuickEntryChange(setQuickOwnerDraw as any, 'remarks')(event.target.value)}
            />
            <button className="btn" type="submit" disabled={saving}>
              {saving ? 'Saving…' : 'Save Owner Draw'}
            </button>
          </form>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Owner Draw"
        message="Are you sure you want to delete this owner draw? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={async () => {
          if (!token || !deleteConfirm.drawId) return;
          try {
            await deleteOwnerDraw(token, deleteConfirm.drawId);
            showToast('Owner draw deleted successfully.', 'success');
            setDeleteConfirm({ isOpen: false, drawId: null });
            loadMonth();
          } catch (err) {
            showToast(err instanceof Error ? err.message : 'Failed to delete owner draw.', 'error');
            setDeleteConfirm({ isOpen: false, drawId: null });
          }
        }}
        onCancel={() => setDeleteConfirm({ isOpen: false, drawId: null })}
      />

      <ConfirmDialog
        isOpen={recomputeConfirm}
        title="Force Recompute"
        message="This month is locked. Do you want to force a recompute? Manual overrides may be replaced."
        confirmText="Force Recompute"
        cancelText="Cancel"
        type="warning"
        onConfirm={() => {
          setRecomputeConfirm(false);
          performRecompute(true);
        }}
        onCancel={() => setRecomputeConfirm(false)}
      />
    </AppLayout>
  );
};

type ModalProps = {
  title: string;
  onClose: () => void;
  children: ReactNode;
};

const Modal: FC<ModalProps> = ({ title, onClose, children }) => {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-mobile" onClick={(event) => event.stopPropagation()}>
        <header className="modal-header">
          <h3>{title}</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-content">{children}</div>
      </div>
    </div>
  );
};

